/**
 * RichDragHandleMenu — F 371.
 *
 * WHAT THIS REPLACES
 * ------------------
 * BlockNote 0.47.3's default drag-handle menu (the 6-dot). MEASURED on
 * production, in real Chromium, with the menu open:
 *
 *     dropdownText: ["Delete\nColors"]
 *
 * Two items. That is BlockNote's `DragHandleMenu` default, and it is
 * exactly what the operator saw. The dispatch asks for Turn into,
 * Duplicate, Move to, Comment, Suggest edits and Delete.
 *
 * HOW THE OVERRIDE WORKS (and why this is not a fork)
 * ---------------------------------------------------
 * `SideMenuProps` is `{ dragHandleMenu?: FC }` — a documented override
 * point. `DragHandleButton` renders `props.dragHandleMenu || DragHandleMenu`
 * inside `Components.Generic.Menu.Root`, and `DragHandleMenu` renders
 * `Components.Generic.Menu.Dropdown` around `props.children`.
 *
 * So the override is: pass our own `dragHandleMenu` FC to the side menu.
 * We render `Components.Generic.Menu.Dropdown` ourselves with our items as
 * children. BlockNote still owns the 6-dot button, its drag behaviour, the
 * menu's open/close, the focus freeze and the positioning.
 *
 * WHY WE USE `Components.Generic.Menu` AND NOT MANTINE'S `<Menu.Item>`
 * --------------------------------------------------------------------
 * `Components.Generic.Menu.Item` is the theme-agnostic API. The Mantine
 * implementation behind it takes an `icon` prop, NOT `leftSection`, and
 * `Menu.Root` calls `assertEmpty(rest)` — which THROWS on any
 * unrecognised prop. A Mantine `<Menu.Item leftSection=… data-testid=…>`
 * inside a BlockNote dropdown would therefore be wrong twice over.
 * The generic API also means this component keeps working if the app ever
 * swaps themes.
 *
 * The generic `Menu.Item` accepts `className`, `icon`, `checked`,
 * `onClick` and `children` — and nothing else. `data-testid` is NOT in the
 * list, so the test hooks are `className`s (`fc-blockmenu-*`), which is
 * the same technique the rest of this app uses.
 *
 * THE SUBMENU PATTERN
 * -------------------
 * "Turn into" and "Move to" open a submenu. The structure is copied from
 * BlockNote's own `BlockColorsItem`, which is the one submenu this app
 * already ships and has already proven to work:
 *
 *   Menu.Root(sub) → Menu.Trigger(sub) → Menu.Item(subTrigger)
 *                  → Menu.Dropdown(sub)
 *
 * THE BLOCKED BLOCK — how we know WHICH block the 6-dot is on
 * -----------------------------------------------------------
 * This is the part that is easy to get wrong, so it is worth stating.
 *
 * The text cursor is NOT the answer. The 6-dot appears on HOVER, and the
 * hovered block can be a different block from the one holding the cursor.
 * Measured in F 371 probe 8: the cursor was placed in paragraph 1, the
 * mouse hovered paragraph 4, and with the menu open the DOM selection was
 * empty (`selectionText: ""`, `selectionAnchorNodeText: ""`) because
 * opening a menu blurs the editor. `editor.getSelection()` therefore
 * returns nothing usable at menu-open time.
 *
 * The DOM `data-id` is not the answer either — it is the right answer for
 * a *selector* but it does not give us the block object we need to pass to
 * `updateBlock` / `insertBlocks` / `removeBlocks`.
 *
 * The supported answer is the extension store. BlockNote's OWN default
 * items use it:
 *
 *   node_modules/@blocknote/react/src/components/SideMenu/DragHandleMenu/
 *     DefaultItems/RemoveBlockItem.tsx
 *       const block = useExtensionState(SideMenuExtension, {
 *         editor, selector: (state) => state?.block });
 *
 * `useExtensionState` and `SideMenuExtension` are both public exports
 * (`@blocknote/react` hooks/useExtension.js, `@blocknote/core/extensions`).
 * We use the identical mechanism, so our items act on the same block
 * BlockNote's own Delete acts on.
 *
 * WHAT IS REAL AND WHAT IS A STUB
 * -------------------------------
 *   Turn into      REAL — `updateBlock` to heading/paragraph/quote. The
 *                  same API the slash menu uses.
 *   Duplicate      REAL — `insertBlocks` of a copy of the block.
 *   Move to        REAL when the host supplies `moveTargets` + `onMoveTo`;
 *                  otherwise rendered as an explicitly unavailable item.
 *   Comment        STUB — there is no comments backend in this app.
 *   Suggest edits  STUB — suggestion mode needs a collaboration server.
 *   Delete         REAL — `removeBlocks`, the same call BlockNote's own
 *                  default Delete item makes.
 *   Colors         KEPT — BlockNote's own `BlockColorsItem`, untouched.
 *
 * A stubbed item is NOT a silent no-op: its label says "(not available)"
 * and it is styled with `fc-blockmenu-unavailable`, so an operator can see
 * at a glance that it is not wired up. It never pretends to have worked.
 */
import React, { useCallback, useState } from 'react';
import {
  BlockColorsItem,
  DragHandleMenu,
  RemoveBlockItem,
  useBlockNoteEditor,
  useComponentsContext,
  useExtensionState,
} from '@blocknote/react';
import { SideMenuExtension } from '@blocknote/core/extensions';
import { IconLock, IconLockOpen } from '@tabler/icons-react';
import { useBlockSeal } from './BlockSealController';
import { isBlockEncrypted } from '../../../core/credentials/blockEncryption';

export interface RichDragHandleMenuProps {
  /** Page titles the block may be moved to. Empty ⇒ "Move to" is unavailable. */
  moveTargets?: { id: string; title: string }[];
  /**
   * Move the block into another page.
   *
   * The block id is passed in rather than looked up again, because the
   * component already knows it — it is the whole reason this component
   * reads the side-menu extension store. Making the caller re-derive it
   * would be a second source of truth for the same fact.
   */
  onMoveTo?: (pageId: string, blockId: string) => void;
  /**
   * F 429 — the id of the page this menu lives in, so the engine can find
   * the page's `Block[]` and persist the sealed body to the right place.
   *
   * When absent, the "Encrypted Block" item is rendered as a STUB and the
   * label says "(not available)" — the same convention already used for
   * Comment / Suggest edits. A page-level editor that has not been told its
   * `pageId` is in that state, and silently encrypting to a wrong page is
   * worse than refusing to show the item.
   */
  pageId?: string | null;
  /**
   * Called after a successful encrypt or decrypt. The parent uses this to
   * trigger a save and to refresh anchor awareness in any external UI.
   *
   * F 429 — kept as a callback rather than wiring the editor update
   * INSIDE this component, because the menu's editor reference is the
   * side-menu store's `editor`, and the page editor is the authority on
   * what `editor.updateBlock` does for the document. The caller decides
   * whether to call `editor.updateBlock(block, stub)` or to reload from
   * storage.
   */
  onEncryptedBlockChange?: (blockId: string, action: 'encrypt' | 'decrypt') => void;
}

/** The heading levels "Turn into" offers, in Notion's order. */
const HEADING_LEVELS = [
  { level: 1, label: 'Heading 1' },
  { level: 2, label: 'Heading 2' },
  { level: 3, label: 'Heading 3' },
] as const;

export const RichDragHandleMenu: React.FC<RichDragHandleMenuProps> = ({
  moveTargets,
  onMoveTo,
  pageId,
  onEncryptedBlockChange,
}) => {
  const Components = useComponentsContext()!;
  const editor = useBlockNoteEditor();

  // The block the 6-dot is attached to — the SAME source BlockNote's own
  // default Delete item reads. See the file header for why the text cursor
  // and the DOM are both wrong answers here.
  const block = useExtensionState(SideMenuExtension, {
    editor,
    selector: (state) => state?.block,
  });

  const setBlockType = useCallback(
    (type: 'paragraph' | 'heading' | 'quote', level?: 1 | 2 | 3) => {
      if (!block) return;
      if (type === 'heading') {
        editor.updateBlock(block, { type: 'heading', props: { level: level ?? 1 } });
      } else {
        editor.updateBlock(block, { type });
      }
    },
    [editor, block],
  );

  const duplicate = useCallback(() => {
    if (!block) return;
    const copy = JSON.parse(JSON.stringify(block));
    // A duplicated block needs its own identity. BlockNote assigns ids on
    // insert, but the copied id must not travel with it.
    delete (copy as { id?: string }).id;
    editor.insertBlocks([copy], block, 'after');
  }, [editor, block]);

  const remove = useCallback(() => {
    if (!block) return;
    editor.removeBlocks([block]);
  }, [editor, block]);

  // ── F 487 — encrypted block ────────────────────────────────────────────
  //
  // F 429 kept the seal dialog's state HERE, with this comment:
  //
  //   "The menu mounts only while the 6-dot is open, so a single set of
  //    state variables at this scope is enough."
  //
  // MEASURED FALSE IN THE PRODUCTION BROWSER (2026-10-25, build 8e305ac).
  // The menu mounts only while the 6-dot is open — AND CLICKING A MENU ITEM
  // CLOSES THE MENU. BlockNote's `SideMenuController` renders this menu as
  // `{block?.id && <Component />}`, so the close unmounts THIS COMPONENT,
  // taking `encModalOpen` with it. Measured timeline after the click:
  // the menu item was gone at t+30ms, the side menu at t+250ms, and the
  // modal was absent at every sample. The operator saw nothing happen.
  //
  // So the state moved to `BlockSealController`, which `PageEditor` renders
  // OUTSIDE `<BlockNoteView>`. The menu now just CALLS it. See
  // `BlockSealController.tsx` for the full measurement.
  const seal = useBlockSeal();

  // The current state for THIS particular block — refreshed from the page's
  // `Block[]` whenever the menu opens, not cached from stale React state.
  // `null` means unknown (pageId missing or storage unreadable).
  const [encState, setEncState] = useState<'unknown' | 'plain' | 'sealed'>('unknown');

  const refreshEncState = useCallback(() => {
    if (!pageId || !block) { setEncState('unknown'); return; }
    setEncState(isBlockEncrypted(pageId, block.id) ? 'sealed' : 'plain');
  }, [pageId, block]);

  // Refresh state when the menu opens OR the active block changes.
  // Both happen on every side-menu hover, so the user-visible state is
  // always what the page's storage currently says, not a stale React memo.
  React.useEffect(() => { refreshEncState(); }, [refreshEncState]);

  // The dispatch asks for a Turn-into menu item; we render it even when
  // the block is already encrypted (the label switches to "Decrypt").
  //
  // `canEncryptBlock` is the ACTIONABILITY test, and it now has a third
  // condition: the seal controller must be mounted. Without a provider
  // there is nowhere to open the dialog, so the item says so rather than
  // swallowing the click. F 429 rendered a normal-looking item that did
  // nothing; that is the shape of the bug this dispatch is fixing.
  const canEncryptBlock = Boolean(pageId) && Boolean(block) && Boolean(seal);
  const blockIsSealed = encState === 'sealed';

  const openEncryptModal = useCallback(() => {
    if (!pageId || !block) return;
    refreshEncState();
    seal?.openSealDialog(pageId, block.id);
  }, [pageId, block, refreshEncState, seal]);

  const canMove = Boolean(onMoveTo) && Boolean(block) && Boolean(moveTargets && moveTargets.length > 0);

  return (
    <>
    <DragHandleMenu>
      {/* ── Turn into ─────────────────────────────────────────────────── */}
      <Components.Generic.Menu.Root position="right" sub>
        <Components.Generic.Menu.Trigger sub>
          <Components.Generic.Menu.Item
            className="bn-menu-item fc-blockmenu-turn-into"
            subTrigger
          >
            Turn into
          </Components.Generic.Menu.Item>
        </Components.Generic.Menu.Trigger>
        <Components.Generic.Menu.Dropdown sub>
          <Components.Generic.Menu.Item
            className="bn-menu-item fc-blockmenu-turn-into-text"
            onClick={() => setBlockType('paragraph')}
          >
            Text
          </Components.Generic.Menu.Item>
          {HEADING_LEVELS.map(({ level, label }) => (
            <Components.Generic.Menu.Item
              key={level}
              className={`bn-menu-item fc-blockmenu-turn-into-h${level}`}
              onClick={() => setBlockType('heading', level)}
            >
              {label}
            </Components.Generic.Menu.Item>
          ))}
          <Components.Generic.Menu.Item
            className="bn-menu-item fc-blockmenu-turn-into-quote"
            onClick={() => setBlockType('quote')}
          >
            Quote
          </Components.Generic.Menu.Item>
          <Components.Generic.Menu.Item
            className={`bn-menu-item fc-blockmenu-encrypt ${canEncryptBlock ? '' : 'fc-blockmenu-unavailable'}`}
            onClick={() => {
              if (!canEncryptBlock) return;
              openEncryptModal();
            }}
          >
            {blockIsSealed ? (
              <>
                <IconLockOpen size={14} /> Decrypt block
              </>
            ) : (
              <>
                <IconLock size={14} /> Encrypted Block
              </>
            )}
            {/* F 487 — an unavailable item now SAYS so, in the same words the
                neighbouring Comment and Suggest edits use. F 429 styled the
                disabled state with a CSS class only, so the item read as
                normal and the operator's only clue was that nothing happened.
                A disabled control that looks enabled is a lie in the UI. */}
            {!canEncryptBlock ? ' (not available)' : ''}
          </Components.Generic.Menu.Item>
        </Components.Generic.Menu.Dropdown>
      </Components.Generic.Menu.Root>

      {/* ── Duplicate ─────────────────────────────────────────────────── */}
      <Components.Generic.Menu.Item
        className="bn-menu-item fc-blockmenu-duplicate"
        onClick={duplicate}
      >
        Duplicate
      </Components.Generic.Menu.Item>

      {/* ── Move to ───────────────────────────────────────────────────── */}
      {canMove ? (
        <Components.Generic.Menu.Root position="right" sub>
          <Components.Generic.Menu.Trigger sub>
            <Components.Generic.Menu.Item
              className="bn-menu-item fc-blockmenu-move-to"
              subTrigger
            >
              Move to
            </Components.Generic.Menu.Item>
          </Components.Generic.Menu.Trigger>
          <Components.Generic.Menu.Dropdown sub>
            {moveTargets!.map((t) => (
              <Components.Generic.Menu.Item
                key={t.id}
                className="bn-menu-item fc-blockmenu-move-target"
                onClick={() => onMoveTo!(t.id, block!.id)}
              >
                {t.title}
              </Components.Generic.Menu.Item>
            ))}
          </Components.Generic.Menu.Dropdown>
        </Components.Generic.Menu.Root>
      ) : (
        <Components.Generic.Menu.Item
          className="bn-menu-item fc-blockmenu-move-to fc-blockmenu-unavailable"
          onClick={() => {
            /* Intentionally inert — see the file header. The label says so. */
          }}
        >
          Move to (not available)
        </Components.Generic.Menu.Item>
      )}

      {/* ── Comment ───────────────────────────────────────────────────── */}
      <Components.Generic.Menu.Item
        className="bn-menu-item fc-blockmenu-comment fc-blockmenu-unavailable"
        onClick={() => {
          /* Intentionally inert — no comments backend in this app. */
        }}
      >
        Comment (not available)
      </Components.Generic.Menu.Item>

      {/* ── Suggest edits ─────────────────────────────────────────────── */}
      <Components.Generic.Menu.Item
        className="bn-menu-item fc-blockmenu-suggest-edits fc-blockmenu-unavailable"
        onClick={() => {
          /* Intentionally inert — suggestion mode needs a collab server. */
        }}
      >
        Suggest edits (not available)
      </Components.Generic.Menu.Item>

      {/* ── Colors — BlockNote's own item, untouched ──────────────────── */}
      <BlockColorsItem>Colors</BlockColorsItem>

      {/* ── Delete — BlockNote's own item, untouched ──────────────────── */}
      <RemoveBlockItem>Delete</RemoveBlockItem>
    </DragHandleMenu>

    {/* F 487 — the seal dialog is NO LONGER rendered here.
        F 429 put it here, as a sibling of `DragHandleMenu`, on the reasoning
        that "the modal is portalled by Mantine onto document.body, so it
        survives the menu closing". The portal is true and it is not enough:
        the MODAL'S STATE lived in this component, and this component is
        unmounted by the very click that opens the dialog. The dialog now
        lives in `BlockSealController`, mounted by `PageEditor` OUTSIDE
        `<BlockNoteView>`. */}
    </>
  );
};

export default RichDragHandleMenu;
